import type React from "react";
import {
  Box,
  Chip,
  Divider,
  FormControl,
  IconButton,
  InputBase,
  Menu,
  MenuItem,
  Select,
  Typography,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Tooltip,
} from "@mui/material";
import { Button, DataGrid } from "../../components/common/Index";
import {
  AddCircleOutlineOutlined,
  CalendarMonthOutlined,
  FilterListOutlined,
  KeyboardArrowDownOutlined,
  ListOutlined,
  SearchOutlined,
  EditOutlined,
  DeleteOutline,
  SendOutlined,
} from "@mui/icons-material";
import type {
  GridColDef,
  GridPaginationModel,
  GridRowParams,
} from "@mui/x-data-grid";
import { useCallback, useEffect, useRef, useState } from "react";
import type { Booking, FrontendStatus } from "../../types/bookings";
import { ServiceType, type ServiceTypeKey } from "../../types/service-type";
import { useNavigate } from "react-router-dom";
import apiService from "../../service/Api";
import { toast } from "react-toastify";
import { motion } from "framer-motion";
import { useSelector, useDispatch } from "react-redux";
import type { RootState } from "../../store/Index";
import { logout } from "../../store/slice/AuthSlice";
import type {
  BookingResponse,
  BookingsByTypeProps,
  BackendStatus,
} from "./types/list-bookings";
// import CalendarView from "../../components/common/CalendarView";
import WeeklyCalendar from "../../components/common/WeeklyCalendarView";
// import { getStatusColor } from "../../utils/GetStatusColor";
import formatDate from "../../utils/FormatDate";

const statusMapToFrontend: Record<BackendStatus, FrontendStatus> = {
  pending: "enquiry",
  confirmed: "confirmed",
  completed: "fulfilled",
  cancelled: "cancelled",
  lost: "lost",
  testing: "testing",
};

const statusMapToBackend: Record<FrontendStatus, BackendStatus> = {
  enquiry: "pending",
  confirmed: "confirmed",
  fulfilled: "completed",
  cancelled: "cancelled",
  lost: "lost",
  testing: "testing",
};

const ListBookings = ({
  bookingType,
  defaultServiceType,
}: BookingsByTypeProps) => {
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const userPermissions = useSelector(
    (state: RootState) => state.auth.user?.permissions || [],
  );
  const dispatch = useDispatch();
  const [inputValue, setInputValue] = useState<string>("");
  const [appliedSearchTerm, setAppliedSearchTerm] = useState<string>("");
  const [viewMode, setViewMode] = useState<"table" | "calendar" | "kanban">(
    "table",
  );
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [bookings, setBookings] = useState<Booking[]>([]);

  const [rowCount, setRowCount] = useState(0);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10,
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);
  const [bookingToDelete, setBookingToDelete] = useState<number | null>(null);

  const navigate = useNavigate();
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);

  // Helper function to check permissions
  const hasPermission = (permission: string) => {
    return userPermissions.includes(permission.trim().replace(/'$/, ""));
  };

  // Handle status change
  const handleStatusChange = async (id: number, newStatus: FrontendStatus) => {
    try {
      const backendStatus = statusMapToBackend[newStatus];
      await apiService.patch(`/api/bookings/booking/${id}/`, {
        status: backendStatus,
      });
      setBookings((prevBookings) =>
        prevBookings.map((booking) =>
          booking.id === id ? { ...booking, status: newStatus } : booking,
        ),
      );
      toast.success("Status updated successfully", {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
    } catch (err: unknown) {
      setError("Failed to update status");
      toast.error("Failed to update status", {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
      console.error("Status update error:", err);
    }
  };

  const handlePaymentStatusChange = async (
    id: number,
    newPaymentStatus: string,
  ) => {
    try {
      await apiService.patch(`/api/bookings/booking/${id}/`, {
        payment_status: newPaymentStatus.toLowerCase(),
      });

      setBookings((prevBookings) =>
        prevBookings.map((booking) =>
          booking.id === id
            ? { ...booking, paymentStatus: newPaymentStatus }
            : booking,
        ),
      );

      toast.success("Payment status updated successfully", {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
    } catch (err: unknown) {
      setError("Failed to update payment status");
      toast.error("Failed to update payment status", {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
      console.error("Payment status update error:", err);
    }
  };

  // Handle delete confirmation dialog
  const confirmDelete = (id: number) => {
    setBookingToDelete(id);
    setOpenDeleteDialog(true);
  };

  const handleDelete = async () => {
    if (bookingToDelete === null) return;
    try {
      await apiService.delete(`/api/bookings/booking/${bookingToDelete}/`);
      setBookings((prevBookings) =>
        prevBookings.filter((booking) => booking.id !== bookingToDelete),
      );
      toast.success("Booking deleted successfully", {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
      setOpenDeleteDialog(false);
      setBookingToDelete(null);
      fetchBookings();
    } catch (err: unknown) {
      setError("Failed to delete booking");
      toast.error("Failed to delete booking", {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
      console.error("Delete booking error:", err);
      setOpenDeleteDialog(false);
      setBookingToDelete(null);
    }
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setBookingToDelete(null);
  };

  const fetchBookings = useCallback(async () => {
    setLoading(true);
    try {
      const apiPage = paginationModel.page + 1;
      const params: Record<string, string | number> = {
        page: apiPage,
        page_size: paginationModel.pageSize,
        search: appliedSearchTerm,
        ordering: "-id",
      };

      if (bookingType) {
        params.booking_type = bookingType;
      }

      const response = await apiService.get<{
        count: number;
        results: BookingResponse[];
      }>("/api/bookings/booking/?page_size=500", params);

      const fetchedBookings: Booking[] = response.data.results
        .filter((booking): booking is BookingResponse => {
          if (!booking || typeof booking !== "object" || !booking.id) {
            console.warn("Invalid booking data filtered out:", booking);
            return false;
          }
          return true;
        })
        .map((booking) => {
          let computedTitle: string;

          if (
            booking.booking_type &&
            booking.booking_type.toUpperCase() === "CHURCH" &&
            booking.services &&
            booking.services.length > 0 &&
            booking.services[0].church_name
          ) {
            computedTitle = booking?.title || "";
          } else if (
            booking.booking_type &&
            booking.booking_type.toUpperCase() === "CONFERENCE" &&
            booking.events.length > 0
          ) {
            computedTitle = booking.events[0].title || "";
          } else if (booking.deceased && booking.deceased.length > 0) {
            const firstDeceased = booking.deceased[0];
            const deceasedName = firstDeceased.first_name || "";
            const deceasedSurname = firstDeceased.surname || "";
            computedTitle =
              `${deceasedName} ${deceasedSurname}`.trim() ||
              "Deceased Name N/A";
          } else if (
            booking.client &&
            (booking.client.salutation || booking.client.company_name)
          ) {
            computedTitle = `${booking.client.salutation || ""} ${
              booking.client.company_name || ""
            }`.trim();
          } else if (booking.title) {
            computedTitle = booking.title;
          } else {
            computedTitle = "Booking N/A";
          }

          const service =
            booking.service && booking.service.length < 50
              ? booking.service
              : booking.booking_type && booking.booking_type.length < 50
                ? booking.booking_type
                : "N/A";

          const packageName =
            booking.package_item?.name || "No selected package";

          const paymentStatus = booking.payment_status
            ? booking.payment_status.charAt(0).toUpperCase() +
              booking.payment_status.slice(1)
            : "Unknown";

          const startTime =
            booking.services && booking.services.length > 0
              ? booking.services[0].start_time || "00:00:00"
              : "00:00:00";

          const endTime =
            booking.services && booking.services.length > 0
              ? booking.services[0].end_time || "23:59:59"
              : "23:59:59";

          const validStatuses = [
            "pending",
            "confirmed",
            "completed",
            "cancelled",
            "lost",
            "testing",
          ];

          const status: FrontendStatus = validStatuses.includes(booking.status)
            ? statusMapToFrontend[booking.status as BackendStatus] || "enquiry"
            : "enquiry";

          const bookingType =
            booking.booking_type && booking.booking_type.length < 50
              ? booking.booking_type
              : "N/A";
          const dateOfService = formatDate(
            booking?.services[0]?.date_of_service ||
              `${booking?.events[0]?.start_date_of_event || ""} -${
                booking?.events[0]?.end_date_of_event || ""
              }` ||
              "No date of service",
          );

          return {
            id: booking.id,
            title: computedTitle,
            service,
            package: packageName,
            paymentStatus,
            dateOfService: dateOfService,
            status,
            bookingType,
            startTime,
            endTime,
          };
        });

      setBookings(fetchedBookings);
      setRowCount(response.data.count);
      setError(null);
    } catch (err: unknown) {
      let errorMessage = "Failed to load bookings. Please try again.";
      const errorResponse = (
        err as {
          response?: { status?: number; data?: { detail?: string } };
          message?: string;
          stack?: string;
        }
      )?.response;
      if (errorResponse?.status === 500) {
        errorMessage = "Server error: Unable to retrieve bookings.";
      } else if (errorResponse?.status === 401) {
        errorMessage = "Unauthorized: Please log in again.";
        dispatch(logout());
        navigate("/login");
      } else if (errorResponse?.data?.detail) {
        errorMessage = errorResponse.data.detail;
      }
      setError(errorMessage);
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
      const errorInfo = err as {
        message?: string;
        response?: { data?: unknown };
        stack?: string;
      };
      console.error("Fetch bookings error:", {
        message: errorInfo.message,
        response: errorInfo.response?.data,
        stack: errorInfo.stack,
      });
    } finally {
      setLoading(false);
    }
  }, [appliedSearchTerm, bookingType, dispatch, navigate, paginationModel]);

  // Handle pagination model change from DataGrid
  const handlePaginationModelChange = (newModel: GridPaginationModel) => {
    setPaginationModel(newModel);
  };

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  useEffect(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    if (inputValue !== appliedSearchTerm) {
      debounceTimer.current = setTimeout(() => {
        setAppliedSearchTerm(inputValue);
        // Reset to first page when searching
        setPaginationModel((prev) => ({ ...prev, page: 0 }));
      }, 1000);
    }
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [inputValue, appliedSearchTerm]);

  // Handlers
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
      setAppliedSearchTerm(inputValue);
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
    }
  };

  const handleAddClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const columns: GridColDef<Booking>[] = [
    { field: "id", headerName: "ID", width: 90 },
    {
      field: "title",
      headerName: "Title",
      flex: 1,
    },
    {
      field: "package",
      headerName: "Package",
      flex: 1,
    },
    {
      field: "paymentStatus",
      headerName: "Payment Status",
      flex: 1,
      renderCell: (params) => (
        <FormControl fullWidth>
          <Select
            value={params.row?.paymentStatus || "Pending"}
            onChange={(event) =>
              params.row?.id &&
              handlePaymentStatusChange(
                params.row.id,
                event.target.value as FrontendStatus,
              )
            }
            onClick={(event) => event.stopPropagation()}
            onOpen={(event) => event.stopPropagation()}
            MenuProps={{
              onClick: (event) => event.stopPropagation(),
            }}
            sx={{
              "& .MuiSelect-select": {
                padding: "4px 8px",
                width: "100%",
                color: reduxTheme.neutral_colors?.[100] || "#333",
              },
              "& .MuiOutlinedInput-notchedOutline": {
                border: "none",
              },
            }}
          >
            <MenuItem value="Pending">
              <Chip
                label="Pending"
                sx={{
                  bgcolor: "#F79E1B40",
                  color: "#F79E1B",
                  width: "100%",
                  "& .MuiChip-label": { fontWeight: 500 },
                }}
              />
            </MenuItem>
            <MenuItem value="Paid">
              <Chip
                label="paid"
                sx={{
                  bgcolor: "#4BA66340",
                  color: "#4BA663",
                  width: "100%",
                  "& .MuiChip-label": { fontWeight: 500 },
                }}
              />
            </MenuItem>
            <MenuItem value="Other">
              <Chip
                label="other"
                sx={{
                  bgcolor: "#0000FF26",
                  color: "#0000FF",
                  width: "100%",
                  "& .MuiChip-label": { fontWeight: 500 },
                }}
              />
            </MenuItem>
            <MenuItem value="">
              <Chip
                label=""
                sx={{
                  bgcolor: "#EB433540",
                  color: "#EB4335",
                  width: "100%",
                  "& .MuiChip-label": { fontWeight: 500 },
                }}
              />
            </MenuItem>
          </Select>
        </FormControl>
      ),
    },
    {
      field: "dateOfService",
      headerName: "Date of service",
      flex: 1,
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      renderCell: (params) => (
        <FormControl fullWidth>
          <Select
            value={params.row?.status || "enquiry"}
            onChange={(event) =>
              params.row?.id &&
              handleStatusChange(
                params.row.id,
                event.target.value as FrontendStatus,
              )
            }
            onClick={(event) => event.stopPropagation()}
            onOpen={(event) => event.stopPropagation()}
            MenuProps={{
              onClick: (event) => event.stopPropagation(),
            }}
            sx={{
              "& .MuiSelect-select": {
                padding: "4px 8px",
                width: "100%",
                color: reduxTheme.neutral_colors?.[100] || "#333",
              },
              "& .MuiOutlinedInput-notchedOutline": {
                border: "none",
              },
            }}
          >
            <MenuItem value="enquiry">
              <Chip
                label="Enquiry"
                sx={{
                  bgcolor: "#F79E1B40",
                  color: "#F79E1B",
                  width: "100%",
                  "& .MuiChip-label": { fontWeight: 500 },
                }}
              />
            </MenuItem>
            <MenuItem value="confirmed">
              <Chip
                label="Confirmed"
                sx={{
                  bgcolor: "#4BA66340",
                  color: "#4BA663",
                  width: "100%",
                  "& .MuiChip-label": { fontWeight: 500 },
                }}
              />
            </MenuItem>
            <MenuItem value="fulfilled">
              <Chip
                label="Fulfilled"
                sx={{
                  bgcolor: "#0000FF26",
                  color: "#0000FF",
                  width: "100%",
                  "& .MuiChip-label": { fontWeight: 500 },
                }}
              />
            </MenuItem>
            <MenuItem value="cancelled">
              <Chip
                label="Cancelled"
                sx={{
                  bgcolor: "#EB433540",
                  color: "#EB4335",
                  width: "100%",
                  "& .MuiChip-label": { fontWeight: 500 },
                }}
              />
            </MenuItem>
            <MenuItem value="testing">
              <Chip
                label="Testing"
                sx={{
                  bgcolor: "#9e8383",
                  color: "#3b2e2e",
                  width: "100%",
                  "& .MuiChip-label": { fontWeight: 500 },
                }}
              />
            </MenuItem>
          </Select>
        </FormControl>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      sortable: false,
      width: 200,
      renderCell: (params) => (
        <Box sx={{ display: "flex", gap: 0.5 }}>
          {params.row.status === "enquiry" && (
            <Tooltip title="Send Proposal" arrow>
              <IconButton
                aria-label="send proposal"
                onClick={(event) => {
                  event.stopPropagation();
                  navigate("/proposals/proposals-builder", {
                    state: { bookingId: params.row.id },
                  });
                }}
                sx={{
                  color: reduxTheme.primary_colors?.[400],
                  "&:hover": {
                    backgroundColor:
                      reduxTheme.neutral_colors?.[800] || "#f5f5f5",
                  },
                }}
              >
                <SendOutlined />
              </IconButton>
            </Tooltip>
          )}
          {hasPermission("booking.change_booking") && (
            <IconButton
              aria-label="edit"
              onClick={(event) => {
                event.stopPropagation();
                navigate(`/bookings/${params.row.id}/edit`);
              }}
              sx={{
                color: reduxTheme.primary_colors?.[400] || "#614438",
                "&:hover": {
                  backgroundColor:
                    reduxTheme.neutral_colors?.[800] || "#f5f5f5",
                },
              }}
            >
              <EditOutlined />
            </IconButton>
          )}
          {hasPermission("booking.delete_booking") && (
            <IconButton
              aria-label="delete"
              onClick={(event) => {
                event.stopPropagation();
                confirmDelete(params.row.id);
              }}
              sx={{
                color: "#EB4335",
                "&:hover": {
                  backgroundColor:
                    reduxTheme.neutral_colors?.[800] || "#f5f5f5",
                },
              }}
            >
              <DeleteOutline />
            </IconButton>
          )}
        </Box>
      ),
    },
  ];

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleAddService = (serviceTypeKey: ServiceTypeKey) => {
    navigate(ServiceType[serviceTypeKey].path, {
      state: {
        serviceType: serviceTypeKey,
        defaultBookingType: bookingType,
      },
    });
    handleMenuClose();
  };

  // Filter menu items based on booking type context
  const getMenuItems = () => {
    if (defaultServiceType) {
      const service = ServiceType[defaultServiceType];
      return [
        <MenuItem
          key={defaultServiceType}
          onClick={() => handleAddService(defaultServiceType)}
          sx={{
            width: 175,
            backgroundColor: reduxTheme.neutral_colors?.[900] || "white",
            color: reduxTheme.neutral_colors?.[100] || "#333",
            "&:hover": {
              backgroundColor: reduxTheme.neutral_colors?.[800] || "#f5f5f5",
            },
          }}
        >
          <AddCircleOutlineOutlined sx={{ mr: 1 }} /> {service.label}
        </MenuItem>,
      ];
    }

    return Object.entries(ServiceType).flatMap(([key, service], index, arr) => {
      const items = [
        <MenuItem
          key={key}
          onClick={() => handleAddService(key as ServiceTypeKey)}
          sx={{
            width: 175,
            backgroundColor: reduxTheme.neutral_colors?.[900] || "white",
            color: reduxTheme.neutral_colors?.[100] || "#333",
            "&:hover": {
              backgroundColor: reduxTheme.neutral_colors?.[800] || "#f5f5f5",
            },
          }}
        >
          <AddCircleOutlineOutlined sx={{ mr: 1 }} /> {service.label}
        </MenuItem>,
      ];

      if (index < arr.length - 1) {
        items.push(
          <Divider
            key={`divider-${key}`}
            sx={{
              borderColor: reduxTheme.neutral_colors?.[300] || "#ddd",
            }}
          />,
        );
      }
      return items;
    });
  };

  const menuItems = getMenuItems();
  const onlyOneMenuItem = menuItems.length === 1;

  const handleRowClick = (params: GridRowParams<Booking>) => {
    navigate(`/bookings/${params.row.id}`);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Box>
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.2 }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mb: 3,
              justifyContent: "space-between",
            }}
          >
            <Box
              sx={{
                backgroundColor: reduxTheme.neutral_colors?.[900] || "#fff",
                borderRadius: "8px",
                display: "flex",
                alignItems: "center",
                pl: 1,
                width: "30%",
                border: `1px solid ${
                  reduxTheme.neutral_colors?.[300] || "#ddd"
                }`,
              }}
            >
              <SearchOutlined
                sx={{
                  mr: 1,
                  color: reduxTheme.neutral_colors?.[100] || "#666",
                }}
              />
              <InputBase
                placeholder="Search by title, service, or package..."
                sx={{
                  ml: 1,
                  flex: 1,
                  color: reduxTheme.neutral_colors?.[100] || "#333",
                }}
                value={inputValue}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
              />
              <IconButton
                sx={{
                  color: reduxTheme.neutral_colors?.[100] || "#666",
                  "&:hover": {
                    backgroundColor:
                      reduxTheme.neutral_colors?.[800] || "#f5f5f5",
                  },
                }}
              >
                <FilterListOutlined />
              </IconButton>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Button
                startIcon={<ListOutlined />}
                onClick={() => setViewMode("table")}
                sx={{
                  color:
                    viewMode === "table"
                      ? reduxTheme.primary_colors?.[400] || "#614438"
                      : reduxTheme.primary_colors?.[100] || "transparent",
                  backgroundColor:
                    viewMode === "table"
                      ? reduxTheme.primary_colors?.[500] || "#614438"
                      : reduxTheme.primary_colors?.[800] || "transparent",
                  "&:hover": {
                    backgroundColor:
                      reduxTheme.primary_colors?.[500] || "#503629",
                    color: reduxTheme.neutral_colors?.[100] || "#fff",
                  },
                }}
              >
                Table
              </Button>
              <Button
                startIcon={<CalendarMonthOutlined />}
                onClick={() => setViewMode("calendar")}
                sx={{
                  color:
                    viewMode === "calendar"
                      ? reduxTheme.primary_colors?.[400] || "#614438"
                      : reduxTheme.primary_colors?.[100] || "transparent",
                  backgroundColor:
                    viewMode === "calendar"
                      ? reduxTheme.primary_colors?.[500] || "#614438"
                      : reduxTheme.primary_colors?.[800] || "transparent",
                  "&:hover": {
                    backgroundColor:
                      reduxTheme.primary_colors?.[500] || "#503629",
                    color: reduxTheme.neutral_colors?.[100] || "#fff",
                  },
                }}
              >
                Calendar
              </Button>
              {hasPermission("booking.add_booking") &&
                (onlyOneMenuItem && defaultServiceType ? (
                  <Button
                    startIcon={<AddCircleOutlineOutlined />}
                    variant="contained"
                    onClick={() => handleAddService(defaultServiceType)}
                    sx={{
                      backgroundColor:
                        reduxTheme.primary_colors?.[400] || "#614438",
                      color: reduxTheme.neutral_colors?.[900] || "#fff",
                      "&:hover": {
                        backgroundColor:
                          reduxTheme.primary_colors?.[500] || "#503629",
                      },
                    }}
                  >
                    Add{" "}
                    {bookingType
                      ? bookingType.charAt(0).toUpperCase() +
                        bookingType.slice(1).toLowerCase()
                      : ServiceType[defaultServiceType].label}
                  </Button>
                ) : (
                  <>
                    <Button
                      startIcon={<AddCircleOutlineOutlined />}
                      endIcon={<KeyboardArrowDownOutlined />}
                      onClick={handleAddClick}
                      variant="contained"
                      sx={{
                        backgroundColor:
                          reduxTheme.primary_colors?.[400] || "#614438",
                        color: reduxTheme.neutral_colors?.[100] || "#fff",
                        "&:hover": {
                          backgroundColor:
                            reduxTheme.primary_colors?.[500] || "#503629",
                        },
                      }}
                    >
                      Add Booking
                    </Button>
                    <Menu
                      id="add-booking-menu"
                      anchorEl={anchorEl}
                      open={Boolean(anchorEl)}
                      onClose={handleMenuClose}
                      PaperProps={{
                        sx: {
                          backgroundColor:
                            reduxTheme.neutral_colors?.[900] || "#fff",
                        },
                      }}
                    >
                      {menuItems}
                    </Menu>
                  </>
                ))}
            </Box>
          </Box>
        </motion.div>

        {loading ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            <Box display="flex" justifyContent="center" my={4}>
              <CircularProgress
                sx={{
                  color: reduxTheme.primary_colors?.[400] || "#614438",
                }}
              />
            </Box>
          </motion.div>
        ) : error ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
          >
            <Box textAlign="center" my={4}>
              <Typography
                color="error"
                variant="h6"
                sx={{
                  color: "#EB4335",
                }}
              >
                {error}
              </Typography>
              <Button
                onClick={() => {
                  setError(null);
                  setPaginationModel({ page: 0, pageSize: 10 });
                  fetchBookings();
                }}
                variant="contained"
              >
                Retry
              </Button>
            </Box>
          </motion.div>
        ) : viewMode === "table" ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.3 }}
          >
            <DataGrid
              rows={bookings}
              columns={columns}
              rowCount={rowCount}
              loading={loading}
              paginationMode="server"
              paginationModel={paginationModel}
              onRowClick={handleRowClick}
              onPaginationModelChange={handlePaginationModelChange}
              pageSizeOptions={[10, 25, 50, 100]}
              sx={{
                "& .MuiDataGrid-columnHeaders": {
                  backgroundColor:
                    reduxTheme.primary_colors?.[500] || "#614438",
                  color: reduxTheme.primary_colors?.[100] || "#fff",
                },
                "& .MuiDataGrid-columnHeader": {
                  backgroundColor:
                    reduxTheme.primary_colors?.[500] || "#614438",
                },
                "& .MuiDataGrid-columnHeaderTitle": {
                  color: reduxTheme.primary_colors?.[100] || "#fff",
                },
                "& .MuiDataGrid-footerContainer": {
                  backgroundColor: "#fff",
                  justifyContent: "center",
                  padding: 1,
                },
              }}
            />
          </motion.div>
        ) : viewMode === "calendar" ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4, delay: 0.3 }}
          >
            {/* <CalendarView
              bookings={bookings}
              renderChip={(booking: Booking) => {
                const title = booking.title;

                // Map backend status to frontend status
                const validStatuses = [
                  "pending",
                  "confirmed",
                  "fulfilled",
                  "cancelled",
                  "lost",
                  "testing",
                ];
                const mappedStatus: FrontendStatus = validStatuses.includes(
                  booking.status,
                )
                  ? statusMapToFrontend[booking.status as BackendStatus] ||
                    "fulfilled"
                  : "enquiry";

                return (
                  <Chip
                    label={title}
                    size="small"
                    onClick={() => navigate(`/bookings/${booking.id}`)}
                    sx={{
                      backgroundColor: getStatusColor(mappedStatus).bg,
                      color: getStatusColor(mappedStatus).color,
                      cursor: "pointer",
                      fontWeight: 500,
                    }}
                  />
                );
              }}
            /> */}
            <WeeklyCalendar bookings={bookings}/>
          </motion.div>
        ) : viewMode === "kanban" ? (
          <Box sx={{ minHeight: "60vh" }}>
            {/* <ReusableKanban
              items={tasks}
              columns={taskColumns}
              getItemTitle={(task) => task.title}
              getItemInitial={(task) => task.title.charAt(0).toUpperCase()}
              height="800px"
              renderCardContent={renderTaskCard}
              onStatusChange={handleStatusChange}
              isLoading={loading}
            /> */}
          </Box>
        ) : null}
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={handleCloseDeleteDialog}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">{"Confirm Deletion"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete this booking? This action cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseDeleteDialog}
            sx={{ color: reduxTheme.neutral_colors?.[100] }}
          >
            Cancel
          </Button>
          <Button onClick={handleDelete} sx={{ color: "#EB4335" }} autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </motion.div>
  );
};

export default ListBookings;
